/**
 * sl-hle4.17: Workspace-scoped notification preferences (UserNotificationPreference + NotificationTriggerRegistry).
 *
 * Renders the per-trigger preferences for the user in the current
 * workspace context. Backend (sl-hle4.16) returns only triggers the
 * user is eligible for (role + plan), so the UI never disables — it
 * just shows or hides.
 *
 * SOLID:
 *   - S: One section, one concern (workspace notification triggers).
 *   - D: Depends on GraphQLService + WorkspaceService — never reaches
 *     into the trigger registry directly.
 */
import {
  Button,
  Menu,
  MenuItem,
  notify,
  Skeleton,
  Switch,
} from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { useWorkspaceName } from '@affine/core/components/hooks/use-workspace-info';
import { GraphQLService } from '@affine/core/modules/cloud';
import {
  WorkspaceService,
  WorkspacesService,
} from '@affine/core/modules/workspace';
import {
  isBackendUnavailableError,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { UserFriendlyError } from '@affine/error';
import {
  myNotificationPreferencesInWorkspaceQuery,
  setMyNotificationPreferenceMutation,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  disableMailWebPush,
  enableMailWebPush,
} from '../../../../../modules/notification/services/mail-web-push';
import { useSettingsOfflineIncompleteLabel } from '../../shared/settings-offline-status';
import * as styles from './style.css';

interface PreferenceRow {
  triggerType: string;
  category: string;
  i18nKey: string;
  descriptionI18nKey: string;
  requiredRole: string;
  enabled: boolean;
  channelMail: boolean;
  channelInApp: boolean;
  channelWebPush: boolean;
  supportsMail: boolean;
  supportsInApp: boolean;
  supportsWebPush: boolean;
  accountId: string;
  folderKey: string;
  quietHours: unknown | null;
  thresholds: number[];
  defaultThresholds: number[];
  stored: boolean;
}

export const WorkspaceTriggerSection: FC = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const graphqlService = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const workspacesService = useService(WorkspacesService);
  const currentWorkspaceId = workspaceService.workspace.id;

  const allWorkspaces = useLiveData(workspacesService.list.workspaces$);
  const cloudWorkspaces = useMemo(
    () => allWorkspaces.filter(w => w.flavour !== 'local'),
    [allWorkspaces]
  );
  const hasCloudWorkspace = cloudWorkspaces.length > 0;
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    () =>
      cloudWorkspaces.find(w => w.id === currentWorkspaceId)?.id ??
      cloudWorkspaces[0]?.id ??
      null
  );

  useEffect(() => {
    if (!hasCloudWorkspace) {
      if (selectedWorkspaceId !== null) setSelectedWorkspaceId(null);
      return;
    }
    if (!cloudWorkspaces.some(w => w.id === selectedWorkspaceId)) {
      setSelectedWorkspaceId(cloudWorkspaces[0]?.id ?? null);
    }
  }, [cloudWorkspaces, hasCloudWorkspace, selectedWorkspaceId]);

  useEffect(() => {
    workspacesService.list.revalidate();
  }, [workspacesService]);

  const [prefs, setPrefs] = useState<PreferenceRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());

  const loadPrefs = useAsyncCallback(async () => {
    if (!selectedWorkspaceId) {
      setPrefs([]);
      setErrorMessage(null);
      setLoading(false);
      return;
    }
    if (!backendAvailable) {
      setPrefs([]);
      setErrorMessage(offlineLabel);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMessage(null);
    try {
      const result = await graphqlService.gql({
        query: myNotificationPreferencesInWorkspaceQuery,
        variables: { workspaceId: selectedWorkspaceId },
      });
      setPrefs(result.myNotificationPreferencesInWorkspace);
    } catch (err) {
      if (isBackendUnavailableError(err)) {
        markBackendUnavailable(err);
      }
      const friendly = UserFriendlyError.fromAny(err);
      setErrorMessage(
        friendly.message ?? 'Failed to load notification preferences'
      );
      setPrefs([]);
    } finally {
      setLoading(false);
    }
  }, [backendAvailable, graphqlService, offlineLabel, selectedWorkspaceId]);

  useEffect(() => {
    loadPrefs();
  }, [loadPrefs]);

  const updatePref = useCallback(
    async (
      pref: PreferenceRow,
      patch: {
        enabled?: boolean;
        channelMail?: boolean;
        channelInApp?: boolean;
        channelWebPush?: boolean;
        thresholds?: number[];
      }
    ) => {
      if (!selectedWorkspaceId) return;
      const workspaceId = selectedWorkspaceId;
      const key = pref.triggerType;
      setPending(prev => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      try {
        if (patch.channelWebPush === true) await enableMailWebPush();
        if (patch.channelWebPush === false) await disableMailWebPush();
        const result = await graphqlService.gql({
          query: setMyNotificationPreferenceMutation,
          variables: {
            input: {
              workspaceId,
              triggerType: pref.triggerType,
              ...patch,
            },
          },
        });
        const updated = result.setMyNotificationPreference;
        setPrefs(prev =>
          prev
            ? prev.map(p => (p.triggerType === key ? { ...p, ...updated } : p))
            : prev
        );
      } catch (err) {
        const friendly = UserFriendlyError.fromAny(err);
        notify.error({
          title: t['com.streamlain.setting.notifications.update-failed'](),
          message: friendly.message ?? String(err),
        });
      } finally {
        setPending(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [graphqlService, selectedWorkspaceId, t]
  );

  const selectedMeta = useMemo(
    () => cloudWorkspaces.find(w => w.id === selectedWorkspaceId),
    [cloudWorkspaces, selectedWorkspaceId]
  );
  const selectedName = useWorkspaceName(selectedMeta);
  const showWorkspaceSelector = cloudWorkspaces.length > 1;

  if (!hasCloudWorkspace) {
    return (
      <SettingWrapper
        title={t['com.streamlain.setting.notifications.workspace.title']()}
      >
        <SettingRow
          name={
            t[
              'com.streamlain.setting.notifications.workspace.no-cloud.title'
            ]?.() ?? 'No cloud workspaces'
          }
          desc={
            t[
              'com.streamlain.setting.notifications.workspace.no-cloud.description'
            ]?.() ??
            'Workspace notifications are only available for cloud workspaces.'
          }
        />
      </SettingWrapper>
    );
  }

  return (
    <SettingWrapper
      title={t['com.streamlain.setting.notifications.workspace.title']()}
    >
      {showWorkspaceSelector ? (
        <SettingRow
          name={
            t[
              'com.streamlain.setting.notifications.workspace.selector.label'
            ]?.() ?? 'Workspace'
          }
          desc={
            t[
              'com.streamlain.setting.notifications.workspace.selector.description'
            ]?.() ?? 'Choose which workspace to configure.'
          }
        >
          <Menu
            items={cloudWorkspaces.map(w => (
              <WorkspaceMenuItem
                key={w.id}
                meta={w}
                selected={w.id === selectedWorkspaceId}
                onSelect={() => setSelectedWorkspaceId(w.id)}
              />
            ))}
          >
            <Button>{selectedName ?? selectedWorkspaceId ?? ''}</Button>
          </Menu>
        </SettingRow>
      ) : null}

      {loading && backendAvailable ? (
        <Skeleton height={48} />
      ) : errorMessage ? (
        <SettingRow
          name={t['com.streamlain.setting.notifications.error']()}
          desc={errorMessage}
        />
      ) : !prefs || prefs.length === 0 ? (
        <SettingRow
          name={t[
            'com.streamlain.setting.notifications.workspace.empty.title'
          ]()}
          desc={t[
            'com.streamlain.setting.notifications.workspace.empty.description'
          ]()}
        />
      ) : (
        prefs.map(pref => (
          <TriggerRow
            key={pref.triggerType}
            pref={pref}
            isPending={pending.has(pref.triggerType)}
            onUpdate={patch => {
              void updatePref(pref, patch);
            }}
          />
        ))
      )}
    </SettingWrapper>
  );
};

interface WorkspaceMenuItemProps {
  meta: { id: string; flavour: string };
  selected: boolean;
  onSelect: () => void;
}

const WorkspaceMenuItem: FC<WorkspaceMenuItemProps> = ({
  meta,
  selected,
  onSelect,
}) => {
  const name = useWorkspaceName(meta);
  return (
    <MenuItem onClick={onSelect} selected={selected}>
      {name ?? meta.id}
    </MenuItem>
  );
};

interface TriggerRowProps {
  pref: PreferenceRow;
  isPending: boolean;
  onUpdate: (patch: {
    enabled?: boolean;
    channelMail?: boolean;
    channelInApp?: boolean;
    channelWebPush?: boolean;
    thresholds?: number[];
  }) => void;
}

const TriggerRow: FC<TriggerRowProps> = ({ pref, isPending, onUpdate }) => {
  const t = useI18n();
  const titleKey = pref.i18nKey as Parameters<typeof t.t>[0];
  const descKey = pref.descriptionI18nKey as Parameters<typeof t.t>[0];
  const title = t.t(titleKey) || pref.triggerType;
  const description = t.t(descKey) || '';
  const hasThresholds = pref.defaultThresholds.length > 0;
  const selectedThresholds = new Set(pref.thresholds);
  const canEnable =
    pref.supportsMail || pref.supportsInApp || pref.supportsWebPush;

  return (
    <>
      <SettingRow name={title} desc={description}>
        <Switch
          checked={pref.enabled}
          disabled={isPending || !canEnable}
          onChange={checked => onUpdate({ enabled: checked })}
        />
      </SettingRow>
      {pref.enabled && (
        <>
          {pref.supportsMail && (
            <SettingRow
              name={t['com.streamlain.setting.notifications.channel.email']()}
              desc=""
              spreadCol
            >
              <Switch
                checked={pref.channelMail}
                disabled={isPending}
                onChange={checked => onUpdate({ channelMail: checked })}
              />
            </SettingRow>
          )}
          {pref.supportsInApp && (
            <SettingRow
              name={t['com.streamlain.setting.notifications.channel.in-app']()}
              desc=""
              spreadCol
            >
              <Switch
                checked={pref.channelInApp}
                disabled={isPending}
                onChange={checked => onUpdate({ channelInApp: checked })}
              />
            </SettingRow>
          )}
          {pref.supportsWebPush && (
            <SettingRow
              name={t[
                'com.streamlain.setting.notifications.channel.web-push'
              ]()}
              desc=""
              spreadCol
            >
              <Switch
                checked={pref.channelWebPush}
                disabled={isPending}
                onChange={checked => onUpdate({ channelWebPush: checked })}
              />
            </SettingRow>
          )}
          {hasThresholds && (
            <SettingRow
              name={t[
                'com.streamlain.setting.notifications.thresholds.title'
              ]()}
              desc={t[
                'com.streamlain.setting.notifications.thresholds.description'
              ]()}
              spreadCol={false}
            >
              <div className={styles.thresholdChipRow}>
                {pref.defaultThresholds.map(threshold => {
                  const checked = selectedThresholds.has(threshold);
                  return (
                    <ThresholdChip
                      key={threshold}
                      threshold={threshold}
                      checked={checked}
                      disabled={isPending}
                      onToggle={() => {
                        const next = checked
                          ? pref.thresholds.filter(v => v !== threshold)
                          : [...pref.thresholds, threshold].sort(
                              (a, b) => a - b
                            );
                        onUpdate({ thresholds: next });
                      }}
                    />
                  );
                })}
              </div>
            </SettingRow>
          )}
        </>
      )}
    </>
  );
};

interface ThresholdChipProps {
  threshold: number;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}

const ThresholdChip: FC<ThresholdChipProps> = ({
  threshold,
  checked,
  disabled,
  onToggle,
}) => {
  return (
    <button
      type="button"
      className={styles.thresholdChip}
      onClick={onToggle}
      disabled={disabled}
      data-checked={checked || undefined}
    >
      {threshold}%
    </button>
  );
};
